import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import movementService from '../../services/movementService';
import movementTypeService from '../../services/movementTypeService';
import { investmentSheetCrvService, investmentSheetPhService, investmentSheetCraService } from '../../services/investmentSheetService';
import { Movement, MovementType } from '../../types/Movement';
import { formatCurrency } from '../../utils/format';
import DateInput from '../common/DateInput';
import '../users/UserForm.css';

interface MovementFormProps {
  movement: Movement | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const MovementForm: React.FC<MovementFormProps> = ({ movement, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<Partial<Movement>>({
    investmentSheetId: '',
    productId: '',
    phaseId: '',
    fechaValor: '',
    tipoMovimiento: '',
    entrada: 0,
    salida: 0,
    descripcion: '',
  });

  const [investmentSheets, setInvestmentSheets] = useState<any[]>([]);
  const [movementTypes, setMovementTypes] = useState<MovementType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Campos calculados
  const [retencionIrpf, setRetencionIrpf] = useState(0);
  const [neto, setNeto] = useState(0);

  useEffect(() => {
    loadInvestmentSheets();
    loadMovementTypes();
    if (movement) {
      setFormData(movement);
    }
  }, [movement]);

  useEffect(() => {
    calculateFields();
  }, [formData.entrada, formData.salida, formData.tipoMovimiento]);

  const loadInvestmentSheets = async () => {
    try {
      // Cargar todas las fichas de inversión (CRV, PH, CRA)
      const [crvRes, phRes, craRes] = await Promise.all([
        investmentSheetCrvService.getAll(),
        investmentSheetPhService.getAll(),
        investmentSheetCraService.getAll(),
      ]);
      setInvestmentSheets([...crvRes.data, ...phRes.data, ...craRes.data]);
    } catch (err) {
      console.error('Error loading investment sheets:', err);
    }
  };

  const loadMovementTypes = async () => {
    try {
      const response = await movementTypeService.getAll();
      setMovementTypes(response.data);
    } catch (err) {
      console.error('Error loading movement types:', err);
    }
  };


  const calculateFields = () => {
    const entrada = formData.entrada || 0;
    const salida = formData.salida || 0;
    const tipo = formData.tipoMovimiento || '';

    // Calcular retención IRPF (19% sobre salida si es liquidación o pago de intereses)
    let irpf = 0;
    if (salida > 0 && (tipo === 'LIQUIDACION_INTERESES' || tipo === 'PAGO_INTERESES')) {
      irpf = salida * 0.19;
    }

    // Calcular neto
    let netoCalculado = 0;
    if (entrada > 0) {
      netoCalculado = entrada;
    } else if (salida > 0) {
      netoCalculado = -(salida - irpf);
    }

    setRetencionIrpf(irpf);
    setNeto(netoCalculado);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'investmentSheetId') {
      // Al seleccionar una ficha, cargar automáticamente su producto y fase
      const selectedSheet = investmentSheets.find(sheet => sheet.id === value);
      if (selectedSheet) {
        setFormData((prev) => ({
          ...prev,
          investmentSheetId: value,
          productId: selectedSheet.productId,
          phaseId: selectedSheet.phaseId,
        }));
      } else {
        setFormData((prev) => ({
          ...prev,
          investmentSheetId: value,
          productId: '',
          phaseId: '',
        }));
      }
    } else if (name === 'entrada' || name === 'salida') {
      const numValue = value ? parseFloat(value) : 0;
      setFormData((prev) => ({
        ...prev,
        [name]: numValue,
        // Si se ingresa entrada, salida debe ser 0 y viceversa
        [name === 'entrada' ? 'salida' : 'entrada']: 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (movement?.id) {
        await movementService.update(movement.id, formData);
        showToast('Movimiento actualizado correctamente');
      } else {
        await movementService.create(formData);
        showToast('Movimiento creado correctamente');
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar el movimiento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-form">
      <h3>{movement ? 'Editar Movimiento' : 'Crear Movimiento'}</h3>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <h4>Identificación</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="investmentSheetId">Inversión *</label>
            <select
              id="investmentSheetId"
              name="investmentSheetId"
              value={formData.investmentSheetId}
              onChange={handleChange}
              required
            >
              <option value="">Seleccionar...</option>
              {investmentSheets.map((sheet) => (
                <option key={sheet.id} value={sheet.id}>
                  {sheet.id}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="productId">Producto *</label>
            <input
              type="text"
              id="productId"
              name="productId"
              value={formData.productId}
              disabled
              style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="phaseId">Fase *</label>
            <input
              type="text"
              id="phaseId"
              name="phaseId"
              value={formData.phaseId}
              disabled
              style={{ backgroundColor: '#f3f4f6', cursor: 'not-allowed' }}
            />
          </div>
        </div>

        <h4>Temporal y Clasificación</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaValor">Fecha Valor *</label>
            <DateInput
              id="fechaValor"
              name="fechaValor"
              value={formData.fechaValor}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group span-2">
            <label htmlFor="tipoMovimiento">Tipo de Movimiento *</label>
            <select
              id="tipoMovimiento"
              name="tipoMovimiento"
              value={formData.tipoMovimiento}
              onChange={handleChange}
              required
            >
              <option value="">Seleccionar...</option>
              {movementTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        <h4>Económico</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="entrada">Entrada (€)</label>
            <input
              type="number"
              id="entrada"
              name="entrada"
              value={formData.entrada || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>
          <div className="form-group">
            <label htmlFor="salida">Salida (€)</label>
            <input
              type="number"
              id="salida"
              name="salida"
              value={formData.salida || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>
        </div>

        <h4>Campos Calculados (Solo lectura)</h4>
        <div className="form-row">
          <div className="form-group">
            <label>Retención IRPF (19%)</label>
            <input
              type="text"
              value={formatCurrency(retencionIrpf)}
              disabled
            />
          </div>
          <div className="form-group">
            <label>Neto</label>
            <input
              type="text"
              value={formatCurrency(neto)}
              disabled
            />
          </div>
        </div>

        <h4>Descripción</h4>
        <div className="form-row">
          <div className="form-group span-3">
            <label htmlFor="descripcion">Descripción</label>
            <textarea
              id="descripcion"
              name="descripcion"
              value={formData.descripcion || ''}
              onChange={handleChange}
              rows={3}
            />
          </div>
        </div>

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : movement ? 'Actualizar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MovementForm;
